/**
 * Clients List Page - Financial Budget Management
 *
 * Director-only page showing all clients with their budget performance metrics.
 * Cards are clickable and navigate to individual client dashboards.
 *
 * Features:
 * - Year selector (2024-2025)
 * - Client cards with performance indicators (actual revenue, PROA, variance)
 * - Clickable cards navigate to /clients/:clientCode/dashboard
 * - Loading states and error handling
 *
 * Route: /clients
 * Access: Director, CEO, Admin roles only
 *
 * @see PRD Story 2.5: Enhanced Clients List Page
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Users, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react'
import { getClients } from '@/api/sig-data'
import { listClientBudgets } from '@/lib/services/budgets.service'
import type { ClientResultApiResponse } from '@/types/financial'
import type { ClientBudgetSettings } from '@/types/budgets'

/**
 * Route configuration
 * Note: Access control handled by Sidebar visibility
 */
export const Route = createFileRoute('/_auth/clients/')({
  component: ClientsListPage,
})

/**
 * Combined client data with actual revenue and budget
 */
interface ClientWithBudget {
  clientCode: string
  clientName: string
  actualRevenue: number | null
  targetRevenue: number | null
  variance: number | null
  performancePercent: number | null
}

/**
 * ClientsListPage Component
 *
 * Displays all clients with budget performance metrics.
 * Each client card is clickable and navigates to their dashboard.
 */
function ClientsListPage() {
  const navigate = useNavigate()
  const currentYear = 2025
  const yearOptions = [2024, 2025]
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [clients, setClients] = useState<ClientWithBudget[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch clients data when year changes
  useEffect(() => {
    const fetchClientsData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Fetch both actual revenue and budget data in parallel
        const [sigClients, budgetsList] = await Promise.all([
          getClients(selectedYear.toString()),
          listClientBudgets(selectedYear)
        ])

        // Create a map of client codes to budget settings
        const budgetsMap = new Map<string, ClientBudgetSettings>()
        budgetsList.clients.forEach(budget => {
          budgetsMap.set(budget.clientCode, budget)
        })

        // Merge actual revenue data with budget data
        const mergedClients: ClientWithBudget[] = sigClients.map(sigClient => {
          const budget = budgetsMap.get(sigClient.clientCode)
          const actualRevenue = sigClient.receitas || null
          const targetRevenue = budget?.settings.targetRevenue || null

          // Calculate variance and performance percentage
          let variance: number | null = null
          let performancePercent: number | null = null

          if (actualRevenue !== null && targetRevenue !== null && targetRevenue > 0) {
            variance = ((actualRevenue - targetRevenue) / targetRevenue) * 100
            performancePercent = (actualRevenue / targetRevenue) * 100
          }

          return {
            clientCode: sigClient.clientCode,
            clientName: sigClient.clientName || `Cliente ${sigClient.clientCode}`,
            actualRevenue,
            targetRevenue,
            variance,
            performancePercent
          }
        })

        // Sort by client name
        mergedClients.sort((a, b) => a.clientName.localeCompare(b.clientName))

        setClients(mergedClients)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load clients data'
        setError(errorMessage)
        console.error('Error fetching clients:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchClientsData()
  }, [selectedYear])

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-brand-dark mb-2">
          Clientes - Desempenho Financeiro
        </h1>
        <p className="text-gray-600">
          Visualize o desempenho financeiro de todos os clientes e acesse seus orçamentos detalhados
        </p>
      </div>

      {/* Year Selector */}
      <div className="mb-6 flex items-center gap-4">
        <label htmlFor="year-selector" className="text-sm font-medium text-gray-700">
          Ano:
        </label>
        <div className="flex gap-2">
          {yearOptions.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedYear === year
                  ? 'bg-brand-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card rounded-monumenta p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card rounded-monumenta p-8 text-center border-red-200 bg-red-50">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar clientes</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => setSelectedYear(selectedYear)}
            className="inline-flex items-center gap-2 px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && clients.length === 0 && (
        <div className="card rounded-monumenta p-12 text-center">
          <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            Nenhum cliente encontrado
          </h3>
          <p className="text-gray-600">
            Não há dados de clientes disponíveis para o ano de {selectedYear}.
          </p>
        </div>
      )}

      {/* Clients Grid */}
      {!isLoading && !error && clients.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <div
              key={client.clientCode}
              onClick={() => {
                const targetPath = `/clients/${client.clientCode}/dashboard` as const
                console.log('Navigating to client:', client.clientCode, 'Path:', targetPath)
                // @ts-ignore - TanStack Router typing issue
                navigate({ to: targetPath })
              }}
              className="card card-interactive rounded-monumenta p-6 cursor-pointer transform transition-all hover:scale-105"
              role="button"
              tabIndex={0}
              aria-label={`Ver dashboard de ${client.clientName}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const targetPath = `/clients/${client.clientCode}/dashboard` as const
                  // @ts-ignore - TanStack Router typing issue
                  navigate({ to: targetPath })
                }
              }}
            >
              {/* Client Name */}
              <h3 className="text-lg font-bold text-brand-dark mb-4">
                {client.clientName}
              </h3>

              {/* Performance Metrics */}
              <div className="space-y-3">
                {/* Actual Revenue */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Realizado</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {client.actualRevenue !== null
                      ? new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(client.actualRevenue)
                      : 'N/A'}
                  </p>
                </div>

                {/* PROA Target */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">PROA</p>
                  <p className="text-lg font-medium text-gray-700">
                    {client.targetRevenue !== null
                      ? new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(client.targetRevenue)
                      : 'N/A'}
                  </p>
                </div>

                {/* Variance */}
                {client.variance !== null && (
                  <div className="flex items-center gap-2 pt-2 border-t">
                    {client.variance >= 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${client.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {client.variance >= 0 ? '+' : ''}
                      {client.variance.toFixed(1)}%
                    </span>
                    <span className="text-xs text-gray-500">vs PROA</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

